import { useEffect, useRef, useMemo, useState } from 'react';
import { Clock } from 'lucide-react';
import { isToday } from 'date-fns';
import { useCalendarEvents } from '../../../hooks/useCalendarEvents';
import { useTimelineBlocks } from '../../../hooks/useTimelineBlocks';
import { useAllTasks } from '../../../hooks/useTasks';
import { useAppStore } from '../../../store';
import { timeToMinutes, DEFAULT_DURATION, computeColumnLayouts, computeAdaptiveTimelineRange, formatTimeLabel } from '../../../lib/timeline';
import { TimelineGrid } from './TimelineGrid';
import { TimelineNowIndicator } from './TimelineNowIndicator';
import { TimelineMeetingBlock } from './TimelineMeetingBlock';
import { DraggableTimelineBlock } from './DraggableTimelineBlock';
import { CrossSpaceTimelineBlock } from './CrossSpaceTimelineBlock';
import { TimelineDropZone } from './TimelineDropZone';

interface TimelinePanelProps {
  scrollRef?: React.RefObject<HTMLDivElement | null>;
}

export function TimelinePanel({ scrollRef: externalScrollRef }: TimelinePanelProps) {
  const internalScrollRef = useRef<HTMLDivElement>(null);
  const scrollRef = externalScrollRef ?? internalScrollRef;
  const selectedDate = useAppStore((s) => s.selectedDate);
  const currentSpaceId = useAppStore((s) => s.currentSpaceId);
  const spaces = useAppStore((s) => s.spaces);
  const { data: events = [] } = useCalendarEvents();
  const allBlocks = useTimelineBlocks(selectedDate);
  const allTasks = useAllTasks();
  const [showEarlier, setShowEarlier] = useState(false);
  const [showLater, setShowLater] = useState(false);

  useEffect(() => {
    setShowEarlier(false);
    setShowLater(false);
  }, [selectedDate]);

  const taskMetaById = useMemo(
    () => new Map(allTasks.map((task) => [task.id, task])),
    [allTasks]
  );

  const hydratedBlocks = useMemo(
    () => allBlocks.map((block) => {
      const task = taskMetaById.get(block.taskId);
      if (!task) return block;
      return {
        ...block,
        itemType: block.itemType ?? task.itemType,
        parentId: block.parentId ?? task.parentId ?? null,
      };
    }),
    [allBlocks, taskMetaById]
  );

  const ownBlocks = useMemo(() => hydratedBlocks.filter(b => !b.spaceId || b.spaceId === currentSpaceId), [hydratedBlocks, currentSpaceId]);
  const crossBlocks = useMemo(() => hydratedBlocks.filter(b => b.spaceId && b.spaceId !== currentSpaceId), [hydratedBlocks, currentSpaceId]);

  const activeEvents = useMemo(
    () => events.filter((e) => !e.isCancelled),
    [events]
  );
  const cancelledEvents = useMemo(
    () => events.filter((e) => e.isCancelled),
    [events]
  );

  const columnLayoutItems = useMemo(() => {
    const items: Array<{ id: string; startMin: number; endMin: number }> = [];
    for (const e of activeEvents) {
      const s = timeToMinutes(e.startTime);
      const end = timeToMinutes(e.endTime);
      if (end > s) items.push({ id: e.id, startMin: s, endMin: end });
    }
    for (const b of hydratedBlocks) {
      const s = timeToMinutes(b.scheduledStartTime);
      const dur = b.scheduledDuration ?? DEFAULT_DURATION;
      items.push({ id: b.id, startMin: s, endMin: s + dur });
    }
    return items;
  }, [activeEvents, hydratedBlocks]);

  const visibleRange = useMemo(() => {
    const items = [
      ...events.map((event) => ({
        startMin: timeToMinutes(event.startTime),
        endMin: timeToMinutes(event.endTime),
      })),
      ...hydratedBlocks.map((block) => {
        const startMin = timeToMinutes(block.scheduledStartTime);
        return {
          startMin,
          endMin: startMin + (block.scheduledDuration ?? DEFAULT_DURATION),
        };
      }),
    ];

    return computeAdaptiveTimelineRange(items);
  }, [events, hydratedBlocks]);

  const rangeStartMin = showEarlier ? 0 : visibleRange.startMin;
  const rangeEndMin = showLater ? 24 * 60 : visibleRange.endMin;
  const startHour = rangeStartMin / 60;
  const endHour = rangeEndMin / 60;
  const totalHours = endHour - startHour;
  const pxPerHour = 100;
  const timelineHeight = totalHours * pxPerHour;
  const rangeLabel = `${formatTimeLabel(rangeStartMin)} - ${formatTimeLabel(rangeEndMin)}`;
  const columnLayouts = useMemo(() => computeColumnLayouts(columnLayoutItems), [columnLayoutItems]);

  const toPixel = (minutes: number) => ((minutes - rangeStartMin) * pxPerHour) / 60;
  const durationToPx = (duration: number) => (duration * pxPerHour) / 60;

  return (
    <div
      data-tutorial="timeline-panel"
      className="w-full h-full max-h-full surface-card flex flex-col overflow-hidden"
    >
      {/* Header */}
      <div className="px-3 py-2.5 border-b border-border-subtle shrink-0 flex items-center justify-between gap-3">
        <h3 className="text-[10px] font-semibold text-text-secondary tracking-wide uppercase">Timeline</h3>
        <div className="flex items-center gap-2">
          {visibleRange.startMin > 0 && (
            <button
              type="button"
              onClick={() => setShowEarlier((v) => !v)}
              className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                showEarlier ? 'bg-bg-surface text-text-primary' : 'text-text-muted hover:text-text-secondary hover:bg-bg-raised'
              }`}
            >
              Earlier
            </button>
          )}
          {visibleRange.endMin < 24 * 60 && (
            <button
              type="button"
              onClick={() => setShowLater((v) => !v)}
              className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                showLater ? 'bg-bg-surface text-text-primary' : 'text-text-muted hover:text-text-secondary hover:bg-bg-raised'
              }`}
            >
              Later
            </button>
          )}
          <span className="text-[10px] font-mono text-text-muted">{rangeLabel}</span>
        </div>
      </div>

      {/* Scrollable timeline body */}
      <TimelineDropZone>
        <div
          ref={scrollRef}
          className="relative pt-2 overflow-hidden bg-[linear-gradient(to_right,var(--color-bg-surface)_0_48px,transparent_48px)]"
          style={{ height: timelineHeight + 8 }}
          data-start-hour={startHour}
          data-end-hour={endHour}
          data-total-minutes={rangeEndMin - rangeStartMin}
          data-px-per-hour={pxPerHour}
        >
            <TimelineGrid startHour={startHour} endHour={endHour} toPixel={toPixel} pxPerHour={pxPerHour} />

            {cancelledEvents.map((event) => (
              <TimelineMeetingBlock key={event.id} event={event} toPixel={toPixel} durationToPx={durationToPx} />
            ))}

            {activeEvents.map((event) => (
              <TimelineMeetingBlock key={event.id} event={event} toPixel={toPixel} durationToPx={durationToPx} columnLayout={columnLayouts.get(event.id)} />
            ))}

            {crossBlocks.map((block) => (
              <CrossSpaceTimelineBlock key={block.id} block={block} currentSpaceId={currentSpaceId} spaces={spaces} toPixel={toPixel} durationToPx={durationToPx} columnLayout={columnLayouts.get(block.id)} />
            ))}

            {ownBlocks.map((block) => (
              <DraggableTimelineBlock key={block.id} block={block} toPixel={toPixel} durationToPx={durationToPx} pxPerHour={pxPerHour} columnLayout={columnLayouts.get(block.id)} startHour={startHour} endHour={endHour} />
            ))}

            {isToday(new Date(selectedDate + 'T00:00:00')) && (
              <TimelineNowIndicator startHour={startHour} endHour={endHour} toPixel={toPixel} />
            )}

            {activeEvents.length === 0 && cancelledEvents.length === 0 && hydratedBlocks.length === 0 && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 pointer-events-none">
                <Clock size={24} className="text-text-muted/30" />
                <p className="text-micro text-text-muted/50 text-center px-4">
                  Drag tasks here to plan your day
                </p>
              </div>
            )}
          </div>
        </TimelineDropZone>
    </div>
  );
}
